import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import { DataTable, Filters, FormModal, KV, Link2, Modal, Notice, PageHeader, Pager, Search, Sel, Tabs, arr, dt, errMsg, money, useAction, useGet, useList, useSessions } from '@/features/shared/ops/kit';
import { badge, opts } from '../lib';

/* Shapes: API_CHANGES §5 */
const REQ_STATUSES = ['requested', 'awaiting_payment', 'paid', 'processing', 'ready', 'delivered', 'rejected'];
const label = (s?: string | null) => (s ? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '—');

/** Next steps a request can take from its current status (AlumniController process/deliver). */
function nextActions(status: string): { action: string; label: string; danger?: boolean }[] {
  switch (status) {
    case 'requested':
    case 'awaiting_payment':
      return [{ action: 'mark_paid', label: 'Mark paid' }, { action: 'start', label: 'Start processing' }, { action: 'reject', label: 'Reject', danger: true }];
    case 'paid':
      return [{ action: 'start', label: 'Start processing' }, { action: 'reject', label: 'Reject', danger: true }];
    case 'processing':
      return [{ action: 'ready', label: 'Mark ready' }, { action: 'reject', label: 'Reject', danger: true }];
    default:
      return [];
  }
}

function RequestActions({ r, onAct, onDeliver }: { r: any; onAct: (r: any, action: string) => void; onDeliver: (r: any) => void }) {
  return (
    <span className="flex gap-3 flex-wrap justify-end">
      {nextActions(r.status).map((x) => x.danger
        ? <button key={x.action} type="button" className="text-red-600 text-sm hover:underline" onClick={(e) => { e.stopPropagation(); onAct(r, x.action); }}>{x.label}</button>
        : <Link2 key={x.action} onClick={() => onAct(r, x.action)}>{x.label}</Link2>)}
      {r.status === 'ready' && <Link2 onClick={() => onDeliver(r)}>Deliver</Link2>}
    </span>
  );
}

function useRequestFlow(onDone: () => void) {
  const a = useAction(onDone);
  const [process, setProcess] = useState<null | { r: any; action: string }>(null);
  const [deliver, setDeliver] = useState<any>(null);
  const modals = (
    <>
      {process && (
        <FormModal title={`${label(process.action)} · ${process.r.name ?? 'request'}`} submitLabel="Confirm" onClose={() => setProcess(null)}
          fields={[{ name: 'note', label: process.action === 'reject' ? 'Reason' : 'Note (optional)', type: 'textarea', required: process.action === 'reject', maxLength: 255 }]}
          onSubmit={async (v) => { const r = await call('post', `/school/alumni-requests/${process.r.id}/process`, { body: { action: process.action, ...v } }); a.setMsg(r.message ?? 'Request updated.'); onDone(); }} />
      )}
      {deliver && (
        <FormModal title={`Deliver · ${deliver.name ?? 'request'}`} submitLabel="Mark delivered" onClose={() => setDeliver(null)}
          fields={[
            { name: 'delivered_via', label: 'Delivered by', type: 'select', required: true, options: [{ value: 'pickup', label: 'Picked up at school' }, { value: 'email', label: 'Email' }, { value: 'courier', label: 'Courier' }, { value: 'post', label: 'Post' }] },
            { name: 'note', label: 'Note (e.g. tracking number)', maxLength: 255 },
          ]}
          onSubmit={async (v) => { const r = await call('post', `/school/alumni-requests/${deliver.id}/deliver`, { body: v }); a.setMsg(r.message ?? 'Marked as delivered.'); onDone(); }} />
      )}
    </>
  );
  return { a, modals, act: (r: any, action: string) => setProcess({ r, action }), deliver: (r: any) => setDeliver(r) };
}

const requestCols = (flow: ReturnType<typeof useRequestFlow>, withName = true) => [
  ...(withName ? [{ head: 'Alumnus', cell: (r: any) => <span><span className="block">{r.name}</span><span className="block text-xs text-slate-500">{r.admission_no}</span></span> }] : []),
  { head: 'Document', cell: (r: any) => `${label(r.type)}${r.copies > 1 ? ` × ${r.copies}` : ''}` },
  { head: 'Fee', right: true, cell: (r: any) => money(r.fee_amount) },
  { head: 'Requested', cell: (r: any) => dt(r.created_at) },
  { head: 'Status', cell: (r: any) => <span>{badge(r.status)}{r.delivered_via && <span className="block text-xs text-slate-500">via {r.delivered_via}</span>}</span> },
  { head: '', cell: (r: any) => <RequestActions r={r} onAct={flow.act} onDeliver={flow.deliver} /> },
];

function AlumniDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useGet<any>('alumnus', `/school/alumni/${id}`);
  const flow = useRequestFlow(() => { qc.invalidateQueries({ queryKey: ['alumnus'] }); qc.invalidateQueries({ queryKey: ['alumni-requests'] }); });
  const d = q.data;
  return (
    <Modal title={d?.name ?? 'Alumnus'} onClose={onClose} wide>
      {q.isLoading && <p className="text-sm text-slate-500 py-4 text-center">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {flow.a.banner}
      {d && (
        <div className="space-y-4 text-sm">
          <div className="grid md:grid-cols-2 gap-x-6">
            <div><KV k="Admission no." v={d.admission_no ?? '—'} /><KV k="Final class" v={d.final_class ?? '—'} /><KV k="Graduated" v={d.graduation_session?.name ?? '—'} /></div>
            <div><KV k="Certificate no." v={d.certificate_no ?? '—'} /><KV k="Phone" v={d.contact_phone ?? '—'} /><KV k="Email" v={d.contact_email ?? '—'} /></div>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Document requests</h4>
            <DataTable rows={arr(d.requests)} empty="No requests from this alumnus." cols={requestCols(flow, false)} />
          </div>
        </div>
      )}
      {flow.modals}
    </Modal>
  );
}

function Directory() {
  const sessions = useSessions();
  const [q, setQ] = useState('');
  const [sessionId, setSessionId] = useState('');
  const list = useList('alumni', '/school/alumni', { q, session_id: sessionId });
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Card>
      <Filters>
        <Search value={q} onChange={setQ} placeholder="Name, admission or certificate no." />
        <Sel value={sessionId} onChange={setSessionId} placeholder="Any graduation year" options={opts(arr(sessions.data), (s) => s.name)} />
      </Filters>
      <DataTable loading={list.isLoading} error={list.error} rows={list.rows} empty="No alumni yet. Students appear here after a graduation run." onRow={(r: any) => setOpen(r.id)}
        cols={[
          { head: 'Name', cell: (r: any) => <span><span className="block">{r.name}</span><span className="block text-xs text-slate-500">{r.admission_no}</span></span> },
          { head: 'Final class', cell: (r: any) => r.final_class ?? '—' },
          { head: 'Graduated', cell: (r: any) => r.graduation_session?.name ?? '—' },
          { head: 'Certificate', cell: (r: any) => r.certificate_no ?? '—' },
          { head: 'Contact', cell: (r: any) => r.contact_phone || r.contact_email || '—' },
          { head: 'Requests', right: true, cell: (r: any) => r.requests_count ?? 0 },
        ]} />
      <Pager meta={list.meta} page={list.page} onPage={list.setPage} />
      {open != null && <AlumniDetail id={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}

function Requests() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const list = useList('alumni-requests', '/school/alumni-requests', { status, type });
  const flow = useRequestFlow(() => qc.invalidateQueries({ queryKey: ['alumni-requests'] }));
  return (
    <Card title="Transcript and certificate requests">
      {flow.a.banner}
      <Filters>
        <Sel value={status} onChange={setStatus} placeholder="Any status" options={REQ_STATUSES.map((s) => ({ value: s, label: label(s) }))} />
        <Sel value={type} onChange={setType} placeholder="Any document" options={['transcript', 'testimonial', 'statement_of_result', 'certificate'].map((s) => ({ value: s, label: label(s) }))} />
      </Filters>
      <DataTable loading={list.isLoading} error={list.error} rows={list.rows} empty="No requests." cols={requestCols(flow)} />
      <Pager meta={list.meta} page={list.page} onPage={list.setPage} />
      {flow.modals}
    </Card>
  );
}

export function AlumniPage() {
  const [tab, setTab] = useState('alumni');
  return (
    <div>
      <PageHeader title="Alumni" sub="Graduates and their document requests" />
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'alumni', label: 'Alumni' }, { id: 'requests', label: 'Requests' }]} />
      {tab === 'alumni' && <Directory />}
      {tab === 'requests' && <Requests />}
    </div>
  );
}
